import type { TPermission } from '@semios/app-platform-value-type-definitions'
import {
  VIEW_PESTS_DEGREE_DAYS_ID_insectId,
  VIEW_PESTS_LARVAL_TREND_ID_insectId,
  VIEW_PESTS_PHENOLOGY_ID_insectId,
  VIEW_PESTS_TRAP_CATCHES_ID_insectId,
} from '@semios/app-platform-value-type-definitions'
import { translate } from 'i18n/i18n'
import type { ReactNode } from 'react'
import type { TDegreeDaysInsectId } from 'stores/selectedValueGroupsStore/insectValueGroupIds'
import { degreeDaysInsectIds } from 'stores/selectedValueGroupsStore/insectValueGroupIds'
import { recordByInsectId } from 'stores/selectedValueGroupsStore/insectValueGroupRecords'
import type { TLayer, TSection, TValueGroup } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { valueGroupToSectionDictionary } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { toggleValueGroups } from 'stores/selectedValueGroupsStore/toggleValueGroups'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { checkAuthorization } from 'utils/checkAuthorization'
import { getInsectNameFromId } from 'utils/getInsectNameFromId'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'

export type TNavigationKey =
  | Exclude<
      TLayer,
      | `degree_days_insect_id_${number}`
      | `trap_catches_insect_id_${number}`
      | `larval_trend_insect_id_${number}`
      | `phenology_insect_id_${number}`
      | 'irrigation_activity_pressure'
      | 'irrigation_activity_zones'
      | 'irrigation_activity_flow_meters'
    >
  | TInsectNavigationItems
  | 'irrigation_activity'

export type TNavigationMenuItem =
  | {
      childLayers?: Partial<Record<TLayer, { key: TLayer; translatedTitle: string }>>
      key: TNavigationKey
      label?: ReactNode
      section: TSection
      translatedTitle: string
    }
  | {
      childLayers?: Partial<Record<TLayer, { key: TLayer; translatedTitle: string }>>
      key: TNavigationKey
      label?: ReactNode
      onToggleOn: () => void
      onToggleOff: () => void
      section: TSection
      translatedTitle: string
    }

export const leadingInsectKey = 'insect_id_'

const makeValueGroupNavigationItem = ({
  label,
  permission,
  section,
  translatedTitle,
  valueGroup,
  isUserFreeRegional = false,
  isSystemPermission = false,
}: Omit<NonNullable<TNavigationMenuItem>, 'onToggleOn' | 'onToggleOff' | 'key'> & {
  permission: TPermission
  valueGroup: Exclude<
    TValueGroup,
    | `degree_days_insect_id_${number}`
    | `trap_catches_insect_id_${number}`
    | `larval_trend_insect_id_${number}`
    | `phenology_insect_id_${number}`
    | 'irrigation_activity_pressure'
    | 'irrigation_activity_zones'
    | 'irrigation_activity_flow_meters'
  >
  isUserFreeRegional?: boolean
  isSystemPermission?: boolean
}): TNavigationMenuItem | null => {
  const anyEntityToCheck = isUserFreeRegional ? 'FOR_ANY_ENTITY' : 'FOR_ANY_PROPERTY'

  let entity = anyEntityToCheck

  if (isSystemPermission) {
    entity = '*'
  }

  if (!checkAuthorization({ entity, permission }, undefined)) return null

  return {
    key: valueGroup,
    label,
    onToggleOn: () => toggleValueGroups([valueGroup], true),
    onToggleOff: () => toggleValueGroups([valueGroup], false),
    section,
    translatedTitle,
  }
}

const makeIrrigationActivityNavigationItem = ({
  isUserFreeRegional = false,
}: {
  isUserFreeRegional?: boolean
}): TNavigationMenuItem | null => {
  const anyEntityToCheck = isUserFreeRegional ? 'FOR_ANY_ENTITY' : 'FOR_ANY_PROPERTY'
  const irrigationPressureValueGroup: TValueGroup = 'irrigation_activity_pressure'
  const irrigationZonesValueGroup: TValueGroup = 'irrigation_activity_zones'
  const irrigationFlowMeterValueGroup: TValueGroup = 'irrigation_activity_flow_meters'
  // this is redundant and kind of a hack fix, but currently users are getting "stuck" with irrigation activity
  // in their selected value groups (probably because of overview charts multi select)
  // so lets ensure it gets removed when the checkbox is clicked
  const irrigationActivity = 'irrigation_activity'

  const hasIrrigationPressurePermission = checkAuthorization({
    entity: anyEntityToCheck,
    permission: 'VIEW_IRRIGATION_DETAILS',
  })

  const hasIrrigationZonesPermission = checkAuthorization({
    entity: anyEntityToCheck,
    permission: 'VIEW_IRRIGATION_DETAILS',
  })

  const hasIrrigationFlowMeterPermission = checkAuthorization({
    entity: anyEntityToCheck,
    permission: 'VIEW_IRRIGATION_FLOW_METER_DETAILS',
  })

  if (!hasIrrigationPressurePermission && !hasIrrigationZonesPermission && !hasIrrigationFlowMeterPermission)
    return null

  const childLayers = {} as Partial<Record<TLayer, NonNullable<TNavigationMenuItem['childLayers']>[TLayer]>>

  if (hasIrrigationPressurePermission) {
    childLayers[irrigationPressureValueGroup] = {
      key: irrigationPressureValueGroup,
      translatedTitle: translate.phrases.templates('{{labelA}} - {{labelB}}', {
        labelA: translate.phrases.banyanApp('Irrigation Activity'),
        labelB: translate.phrases.banyanApp('Pressure'),
      }),
    }
  }

  if (hasIrrigationZonesPermission) {
    childLayers[irrigationZonesValueGroup] = {
      key: irrigationZonesValueGroup,
      translatedTitle: translate.phrases.templates('{{labelA}} - {{labelB}}', {
        labelA: translate.phrases.banyanApp('Irrigation Activity'),
        labelB: translate.phrases.banyanApp('Zones'),
      }),
    }
  }

  if (hasIrrigationFlowMeterPermission) {
    childLayers[irrigationFlowMeterValueGroup] = {
      key: irrigationFlowMeterValueGroup,
      translatedTitle: translate.phrases.templates('{{labelA}} - {{labelB}}', {
        labelA: translate.phrases.banyanApp('Irrigation Activity'),
        labelB: translate.phrases.banyanApp('Flow Meters'),
      }),
    }
  }

  return {
    childLayers,
    key: 'irrigation_activity',
    onToggleOn: () => toggleValueGroups(Object.keys(childLayers) as TValueGroup[], true),
    onToggleOff: () =>
      toggleValueGroups(
        [
          irrigationPressureValueGroup,
          irrigationZonesValueGroup,
          irrigationFlowMeterValueGroup,
          // @ts-expect-error
          irrigationActivity,
        ],
        false,
      ),
    section: valueGroupToSectionDictionary.irrigation_activity_pressure,
    translatedTitle: translate.phrases.banyanApp('Irrigation Activity'),
  }
}

const makeInsectNavigationItem = (insectId: number): TNavigationMenuItem | null => {
  const userPermissions = userDetailsStore.getState().permissions
  const degreeDaysPermission = VIEW_PESTS_DEGREE_DAYS_ID_insectId(insectId)
  const trapCatchesPermission = VIEW_PESTS_TRAP_CATCHES_ID_insectId(insectId)
  const larvalTrendPermission = VIEW_PESTS_LARVAL_TREND_ID_insectId(insectId)
  const phenologyPermission = VIEW_PESTS_PHENOLOGY_ID_insectId(insectId)
  const anyEntityToCheck = isUserOnlyAFreeRegionalUser() ? 'FOR_ANY_ENTITY' : 'FOR_ANY_PROPERTY'

  const hasDegreeDaysPermission = checkAuthorization(
    { entity: anyEntityToCheck, permission: degreeDaysPermission },
    userPermissions,
  )

  const hasTrapCatchesPermission = checkAuthorization(
    { entity: anyEntityToCheck, permission: trapCatchesPermission },
    userPermissions,
  )

  const hasLarvalTrendPermission = checkAuthorization(
    { entity: anyEntityToCheck, permission: larvalTrendPermission },
    userPermissions,
  )

  const hasPhenologyPermission = checkAuthorization(
    { entity: 'FOR_ANY_ENTITY', permission: phenologyPermission },
    userPermissions,
  )

  if (
    !hasDegreeDaysPermission &&
    !hasTrapCatchesPermission &&
    !hasLarvalTrendPermission &&
    !hasPhenologyPermission
  )
    return null

  const degreeDaysLayer = `degree_days_insect_id_${insectId}` as TValueGroup
  const trapCatchesLayer = `trap_catches_insect_id_${insectId}` as TValueGroup
  const larvalTrendLayer = `larval_trend_insect_id_${insectId}` as TValueGroup
  const phenologyLayer = `phenology_insect_id_${insectId}` as TValueGroup
  const childLayers: Partial<Record<TLayer, NonNullable<TNavigationMenuItem['childLayers']>[TLayer]>> = {}
  const insectName = getInsectNameFromId(insectId)

  if (hasDegreeDaysPermission) {
    childLayers[degreeDaysLayer as TLayer] = {
      key: degreeDaysLayer,
      translatedTitle: translate.phrases.banyanApp('{{insectName}} Degree Days', { insectName }),
    }
  }

  if (hasTrapCatchesPermission) {
    childLayers[trapCatchesLayer as TLayer] = {
      key: trapCatchesLayer,
      translatedTitle: translate.phrases.banyanApp('{{insectName}} Trap Catches', { insectName }),
    }
  }

  if (hasLarvalTrendPermission) {
    childLayers[larvalTrendLayer as TLayer] = {
      key: larvalTrendLayer,
      translatedTitle: translate.phrases.banyanApp('{{insectName}} Larval Trend', { insectName }),
    }
  }

  if (hasPhenologyPermission) {
    childLayers[phenologyLayer as TLayer] = {
      key: phenologyLayer,
      translatedTitle: translate.phrases.banyanApp('{{insectName}} Phenology', { insectName }),
    }
  }

  return {
    childLayers,
    key: `${leadingInsectKey}${insectId}` as TNavigationKey,
    onToggleOn: () => {
      const selectedLayers: TValueGroup[] = []

      if (hasDegreeDaysPermission) selectedLayers.push(degreeDaysLayer)

      if (hasTrapCatchesPermission) selectedLayers.push(trapCatchesLayer)

      if (hasLarvalTrendPermission) selectedLayers.push(larvalTrendLayer)

      if (hasPhenologyPermission) selectedLayers.push(phenologyLayer)

      toggleValueGroups(selectedLayers, true)
    },
    onToggleOff: () =>
      toggleValueGroups([degreeDaysLayer, trapCatchesLayer, larvalTrendLayer, phenologyLayer], false),
    section: 'insects',
    translatedTitle: getInsectNameFromId(insectId),
  }
}

type TInsectNavigationItems = `${typeof leadingInsectKey}${TDegreeDaysInsectId}`

export const generateFlatNavigationList = (): Partial<Record<TNavigationKey, TNavigationMenuItem>> => {
  const isUserFreeRegional = isUserOnlyAFreeRegionalUser()

  const menuIncludingNulls: Record<TNavigationKey, TNavigationMenuItem | null> = {
    wind_machine_activity: null,
    // environment
    air_temperature: makeValueGroupNavigationItem({
      permission: 'VIEW_WEATHER_DETAILS',
      section: valueGroupToSectionDictionary.air_temperature,
      translatedTitle: translate.phrases.banyanApp('Temperature'),
      valueGroup: 'air_temperature',
      isUserFreeRegional,
    }),
    atmospheric_pressure: null,
    conditions: makeValueGroupNavigationItem({
      permission: 'VIEW_WEATHER_DETAILS',
      section: valueGroupToSectionDictionary.conditions,
      translatedTitle: translate.phrases.banyanApp('Conditions'),
      valueGroup: 'conditions',
    }),
    bee_hours: makeValueGroupNavigationItem({
      permission: 'VIEW_BEE_HOURS_DETAILS',
      section: valueGroupToSectionDictionary.bee_hours,
      translatedTitle: translate.phrases.banyanApp('Bee Hours'),
      valueGroup: 'bee_hours',
    }),
    almond_bloom: makeValueGroupNavigationItem({
      permission: 'VIEW_BLOOM_PREDICTION',
      section: valueGroupToSectionDictionary.almond_bloom,
      translatedTitle: translate.phrases.banyanApp('Almond Bloom'),
      valueGroup: 'almond_bloom',
    }),
    almond_hull_split: makeValueGroupNavigationItem({
      permission: 'VIEW_ALMOND_HULL_SPLIT_SUMMARY',
      section: valueGroupToSectionDictionary.almond_bloom,
      translatedTitle: translate.phrases.banyanApp('Almond Hull Split'),
      valueGroup: 'almond_hull_split',
    }),
    wet_bulb: makeValueGroupNavigationItem({
      permission: 'VIEW_WEATHER_DETAILS',
      section: valueGroupToSectionDictionary.wet_bulb,
      translatedTitle: translate.phrases.banyanApp('Wet Bulb'),
      valueGroup: 'wet_bulb',
      isUserFreeRegional,
    }),
    wind: makeValueGroupNavigationItem({
      permission: 'VIEW_WEATHER_DETAILS',
      section: valueGroupToSectionDictionary.wind,
      translatedTitle: translate.phrases.banyanApp('Wind'),
      valueGroup: 'wind',
      isUserFreeRegional,
    }),
    dew_point: makeValueGroupNavigationItem({
      permission: 'VIEW_WEATHER_DETAILS',
      section: valueGroupToSectionDictionary.dew_point,
      translatedTitle: translate.phrases.banyanApp('Dew Point'),
      valueGroup: 'dew_point',
      isUserFreeRegional,
    }),
    humidity: makeValueGroupNavigationItem({
      permission: 'VIEW_WEATHER_DETAILS',
      section: valueGroupToSectionDictionary.humidity,
      translatedTitle: translate.phrases.banyanApp('Humidity'),
      valueGroup: 'humidity',
      isUserFreeRegional,
    }),
    leaf_wetness: makeValueGroupNavigationItem({
      permission: 'VIEW_WEATHER_DETAILS',
      section: valueGroupToSectionDictionary.leaf_wetness,
      translatedTitle: translate.phrases.banyanApp('Leaf Wetness'),
      valueGroup: 'leaf_wetness',
      isUserFreeRegional,
    }),
    precipitation: makeValueGroupNavigationItem({
      permission: 'VIEW_WEATHER_DETAILS',
      section: valueGroupToSectionDictionary.precipitation,
      translatedTitle: translate.phrases.banyanApp('Rain'),
      valueGroup: 'precipitation',
      isUserFreeRegional,
    }),
    // water management
    evapotranspiration: makeValueGroupNavigationItem({
      permission: 'VIEW_EVAPOTRANSPIRATION_DETAILS',
      section: valueGroupToSectionDictionary.evapotranspiration,
      translatedTitle: translate.phrases.banyanApp('Evapotranspiration'),
      valueGroup: 'evapotranspiration',
      isUserFreeRegional,
    }),
    fruit_growth: makeValueGroupNavigationItem({
      permission: 'VIEW_FRUIT_GROWTH_DETAILS',
      section: valueGroupToSectionDictionary.fruit_growth,
      translatedTitle: translate.phrases.banyanApp('Fruit Growth'),
      valueGroup: 'fruit_growth',
      isUserFreeRegional,
    }),
    irrigation_activity: makeIrrigationActivityNavigationItem({ isUserFreeRegional }),
    plant_stress: makeValueGroupNavigationItem({
      permission: 'VIEW_PLANT_STRESS_DETAILS',
      section: valueGroupToSectionDictionary.plant_stress,
      translatedTitle: translate.phrases.banyanApp('Plant Stress'),
      valueGroup: 'plant_stress',
      isUserFreeRegional,
    }),
    soil: makeValueGroupNavigationItem({
      permission: 'VIEW_SOIL_MOISTURE_DETAILS',
      section: valueGroupToSectionDictionary.soil,
      translatedTitle: translate.phrases.banyanApp('Soil'),
      valueGroup: 'soil',
      isUserFreeRegional,
    }),
    // pests
    ...recordByInsectId(leadingInsectKey, degreeDaysInsectIds)(makeInsectNavigationItem),
    // sprays
    spray_conditions: makeValueGroupNavigationItem({
      permission: 'VIEW_SPRAY_FORECAST',
      section: valueGroupToSectionDictionary.spray_conditions,
      translatedTitle: translate.phrases.banyanApp('Spray Conditions'),
      valueGroup: 'spray_conditions',
      isUserFreeRegional,
    }),
    // crop development
    chill: makeValueGroupNavigationItem({
      permission: 'VIEW_CHILL_DETAILS',
      section: valueGroupToSectionDictionary.chill,
      translatedTitle: translate.phrases.banyanApp('Chill'),
      valueGroup: 'chill',
      isUserFreeRegional,
    }),
    // diseases
    alternaria: makeValueGroupNavigationItem({
      permission: 'VIEW_ALTERNARIA_DETAILS',
      section: valueGroupToSectionDictionary.alternaria,
      translatedTitle: translate.phrases.banyanApp('Alternaria'),
      valueGroup: 'alternaria',
      isUserFreeRegional,
    }),
    cherry_powdery_mildew: makeValueGroupNavigationItem({
      permission: 'VIEW_CHERRY_POWDERY_MILDEW_DETAILS',
      section: 'diseases',
      translatedTitle: translate.phrases.banyanApp('Cherry Powdery Mildew'),
      valueGroup: 'cherry_powdery_mildew',
      isUserFreeRegional,
    }),
    grape_powdery_mildew: makeValueGroupNavigationItem({
      permission: 'VIEW_GRAPE_POWDERY_MILDEW_DETAILS',
      section: 'diseases',
      translatedTitle: translate.phrases.banyanApp('Grape Powdery Mildew'),
      valueGroup: 'grape_powdery_mildew',
      isUserFreeRegional,
    }),
    fire_blight: makeValueGroupNavigationItem({
      permission: 'VIEW_FIRE_BLIGHT_DETAILS',
      section: valueGroupToSectionDictionary.fire_blight,
      translatedTitle: translate.phrases.banyanApp('Fire Blight'),
      valueGroup: 'fire_blight',
    }),
    // equipment
    wind_machine: makeValueGroupNavigationItem({
      permission: 'VIEW_CONTROLLABLE_FROST_FAN_DETAILS',
      section: valueGroupToSectionDictionary.equipment_status,
      translatedTitle: translate.phrases.banyanApp('Wind Machine'),
      valueGroup: 'wind_machine',
      isUserFreeRegional,
    }),
    equipment_status: makeValueGroupNavigationItem({
      permission: 'EDIT_SERVICE_CENTER', // SYSTEM PERMISSION - not a property permission FYI
      label: translate.phrases.banyanApp('Equipment Status'),
      section: valueGroupToSectionDictionary.equipment_status,
      translatedTitle: translate.phrases.banyanApp('Equipment Status'),
      valueGroup: 'equipment_status',
      isSystemPermission: true,
    }),
    ndvi: null,
  }

  const menuitemsWithoutNulls: Partial<Record<TNavigationKey, TNavigationMenuItem>> = {}

  Object.entries(menuIncludingNulls).forEach(([key, value]) => {
    if (value) menuitemsWithoutNulls[key as TNavigationKey] = value
  })

  return menuitemsWithoutNulls
}
